import * as React from "react";
import {
  DatagridBody,
  DatagridConfigurable,
  FunctionField,
  Pagination,
  RecordContextProvider,
  TextField,
  usePermissions,
  useTheme,
} from "react-admin";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import incomeStatementFilterList from "../../components/incomeStatements/filter/incomeStatementFilterList";
import getFirstAndLastDateOfCurrentMonth from "../../utils/firstLastDate";
import DownloadAction from "../../components/tableListActions/DownloadActionNew";
import BalanceSheetAsideList from "../../components/balanceSheets/aside/IncomeStatementAsideList";
import { toCurrencyAccounting } from "../../utils/formatter";
import ManualReportList from "../../components/reports/ManualReportList";

const [firstDate, lastDate] = getFirstAndLastDateOfCurrentMonth();

const MyDatagridRow = ({
  record,
  id,
  onToggleItem,
  children,
  hasBulkActions,
  rowClick,
  selected,
  selectable,
  ...props
}) => {
  return id ? (
    <RecordContextProvider value={record}>
      <TableRow {...props}>
        {/* data columns based on children */}
        {React.Children.map(children, (child) => {
          return React.isValidElement(child) && child.props.source ? (
            <TableCell
              key={`${id}-${child.props.source}`}
              align={child.type.textAlign || child.props.textAlign}
            >
              {child}
            </TableCell>
          ) : null;
        })}
      </TableRow>
    </RecordContextProvider>
  ) : null;
};

const MyDatagridBody = (props) => (
  <DatagridBody {...props} row={<MyDatagridRow />} />
);
const MyDatagridConfigurable = (props) => (
  <DatagridConfigurable {...props} hover body={<MyDatagridBody />} />
);

export default function BalanceSheetsList() {
  const [theme] = useTheme("light");
  const { permissions } = usePermissions();
  const list = React.useMemo(
    () => (
      <ManualReportList
        perPage={100}
        pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
        aside={
          <BalanceSheetAsideList
            defaultFilter={{
              start_date: firstDate,
              end_date: lastDate,
            }}
          />
        }
        title="mine.label.balance_sheet"
        empty={false}
        actions={<DownloadAction detailOnly />}
        filters={incomeStatementFilterList(permissions, { includeViewMode: false }).filter(
          (filterElement) =>
            !["group_ids", "category_ids"].includes(filterElement.props.source)
        )}
        filterDefaultValues={{
          start_date: firstDate,
          end_date: lastDate,
        }}
        requiredFilters={["start_date", "end_date"]}
        sort={{ field: "chart_of_accounts.code", order: "ASC" }}
        sx={{
          flexDirection: "column",
        }}
        // queryOptions={{ meta: { sort: ["transactions.code ASC"] } }} // overide default sort
      >
        <MyDatagridConfigurable
          rowClick={false}
          bulkActionButtons={false}
          // expand={<AllTransactionJournalExpandList />}
          rowSx={(record) => {
            if (record.amount == 0) {
              return {
                color: "#000",
                "& .RaDatagrid-rowCell": {
                  color: theme !== "dark" ? "grey.500" : "rgba(255,255,255,.4)",
                },
                "& .MuiTableCell-root": {
                  color: theme !== "dark" ? "grey.500" : "rgba(255,255,255,.4)",
                },
              };
            }
          }}
        >
          {permissions && permissions.update_company ? (
            <TextField source="company_name" label="mine.label.company" />
          ) : null}
          <FunctionField
            source="code"
            label="mine.label.code"
            render={(record) => {
              let pad = { pl: 0 };
              switch (record.level) {
                case 2:
                  pad.pl = 2;
                  break;
                case 3:
                  pad.pl = 4;
                  break;
                case 4:
                  pad.pl = 6;
                  break;
                case 5:
                  pad.pl = 8;
                  break;
                default:
                  pad.pl = 0;
                  break;
              }
              return (
                <RecordContextProvider value={record}>
                  <TextField source="code" label="mine.label.code" sx={pad} />
                </RecordContextProvider>
              );
            }}
          />
          <FunctionField
            source="name"
            label="mine.label.name"
            render={(record) => {
              let pad = { pl: 0 };
              switch (record.level) {
                case 2:
                  pad.pl = 2;
                  break;
                case 3:
                  pad.pl = 4;
                  break;
                case 4:
                  pad.pl = 6;
                  break;
                case 5:
                  pad.pl = 8;
                  break;
                default:
                  pad.pl = 0;
                  break;
              }
              return (
                <RecordContextProvider value={record}>
                  <TextField source="name" label="mine.label.name" sx={pad} />
                </RecordContextProvider>
              );
            }}
          />
          <FunctionField
            source="amount"
            label="mine.label.amount"
            textAlign="right"
            render={(record) => {
              let pad = { pr: 0 };
              switch (record.level) {
                case 2:
                  pad.pr = 2;
                  break;
                case 3:
                  pad.pr = 4;
                  break;
                case 4:
                  pad.pr = 6;
                  break;
                case 5:
                  pad.pr = 8;
                  break;
                default:
                  pad.pr = 0;
                  break;
              }
              let amountRecord = { amount: 0 };
              if (!record.amount || typeof record.amount === "number") {
                amountRecord.amount = record.amount;
              } else if (
                typeof record.amount === "string" &&
                !isNaN(record.amount)
              ) {
                amountRecord.amount = +record.amount;
              } else {
                amountRecord.amount = record.amount;
              }
              amountRecord.amount = toCurrencyAccounting(amountRecord.amount);
              return (
                <RecordContextProvider value={amountRecord}>
                  <TextField
                    source="amount"
                    label="mine.label.amount"
                    sx={pad}
                    // textAlign="right"
                    emptyText="0"
                  />
                </RecordContextProvider>
              );
            }}
          />
        </MyDatagridConfigurable>
      </ManualReportList>
    ),
    [theme, permissions]
  );

  return list;
}
